import { type ButtonHTMLAttributes, forwardRef } from "react";
import { cn } from "../../lib/cn";

export type TapTargetProps = ButtonHTMLAttributes<HTMLButtonElement>;

// Unstyled <button> wrapper that enforces a WCAG 2.5.8 (AA) tap-area floor
// (24x24 CSS px) plus app-standard focus ring and disabled handling. Aesthetic
// height/padding is layered on top by callers — this only guarantees no
// clickable button falls below the AA target.
//
// Use directly for custom-styled clickable surfaces (logo, theme card, etc).
// For variant-styled buttons (primary/secondary/etc) use <Button>, which is
// built on top of TapTarget.
//
// Intentional exceptions (kept as raw <button>): Switch toggles whose visual
// element is part of a larger clickable <label>, and full-screen modal
// backdrops — neither is a regular interactive button.
export const TapTarget = forwardRef<HTMLButtonElement, TapTargetProps>(
  ({ className, type, ...rest }, ref) => (
    <button
      ref={ref}
      type={type ?? "button"}
      className={cn(
        "min-h-6 min-w-6",
        "focus-visible:outline-2 focus-visible:outline-accent-500 focus-visible:outline-offset-2",
        "disabled:pointer-events-none disabled:opacity-50",
        "cursor-pointer select-none",
        className,
      )}
      {...rest}
    />
  ),
);
TapTarget.displayName = "TapTarget";
